import type { CommerceHttpAdapter } from '../../client/CommerceHttpAdapter.js'
import type { CacheStrategy } from '../../workers/CacheStrategy.js'
import type { ProductDetails, WebProduct, SearchRequest, SearchResult } from '../../types/index.js'

export class CatalogModule {
  constructor(
    private readonly http: CommerceHttpAdapter,
    private readonly cache: CacheStrategy
  ) {}

  /** Récupère le détail d'un produit par sa référence */
  getProduct(reference: string): Promise<ProductDetails> {
    return this.cache.execute(
      `catalog:product:${reference}`,
      () => this.http.get<ProductDetails>(`/commerce/api/pim/products/${encodeURIComponent(reference)}`),
      'stale-while-revalidate'
    )
  }

  /** Récupère une liste de produits par leurs références */
  getProducts(references: string[]): Promise<WebProduct[]> {
    const key = `catalog:products:${references.sort().join(',')}`
    return this.cache.execute(
      key,
      () => this.http.post<WebProduct[]>('/commerce/api/pim/products/batch', { references }),
      'stale-while-revalidate'
    )
  }

  /** Récupère les produits d'une catégorie */
  getCategory(categoryCode: string, pageIndex = 0, pageSize = 20): Promise<SearchResult> {
    const key = `catalog:category:${categoryCode}:${pageIndex}:${pageSize}`
    return this.cache.execute(
      key,
      () => this.http.get<SearchResult>(
        `/commerce/api/pim/categories/${encodeURIComponent(categoryCode)}/products?pageIndex=${pageIndex}&pageSize=${pageSize}`
      ),
      'stale-while-revalidate'
    )
  }

  /** Effectue une recherche full-text */
  search(request: SearchRequest): Promise<SearchResult> {
    const key = `catalog:search:${JSON.stringify(request)}`
    return this.cache.execute(
      key,
      () => this.http.post<SearchResult>('/commerce/api/pim/search', request),
      'cache-first'
    )
  }

  /** Récupère les suggestions de recherche (autocomplete) */
  suggest(query: string): Promise<string[]> {
    return this.cache.execute(
      `catalog:suggest:${query}`,
      () => this.http.get<string[]>(`/commerce/api/pim/search/suggest?q=${encodeURIComponent(query)}`),
      'cache-first',
      2 * 60_000 // 2 minutes pour les suggestions
    )
  }
}
